import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

import { deviceTimeZone } from '../timezone.js';

import type { ApiScope } from '@nedelnik/shared';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

/**
 * Сессия клиента.
 *
 * Access-токен живёт только в памяти вкладки и обновляется по refresh-cookie,
 * которую браузер не отдаёт скриптам. В `localStorage` его класть нельзя: любая
 * XSS в этот момент превращается в кражу постоянного доступа (раздел 6
 * архитектуры).
 */

export interface Identity {
  id: string;
  email: string;
  display_name: string | null;
  device_id: string;
  /**
   * Когда заведена учётная запись: по ней решают, показывать ли подсказки
   * новичку (3.16.10). Необязательное, потому что у входов, сохранённых до
   * появления поля, его нет — и это правильный исход: подсказок они не видят.
   */
  created_at?: string;
}

export interface PasskeySummary {
  id: string;
  name: string | null;
  created_at: string;
  last_used_at: string | null;
  /**
   * Где ключ лежит: «Bitwarden», «Пароли Apple». Считается на сервере из
   * AAGUID аутентификатора; `null` означает «клиент не сказал», а не «нигде».
   */
  provider: string | null;
  /**
   * Домен, к которому ключ привязан (этап 27). Ключ пускает только там, где
   * его завели: браузер не предложит ключ `nedelnik.app` на `onlap.app`.
   */
  rp_id: string;
}

/** Устройство, с которого выполнен вход (3.16.5). */
export interface DeviceSummary {
  id: string;
  kind: 'web' | 'ios' | 'agent';
  name: string | null;
  created_at: string;
  last_seen_at: string | null;
  /** Есть ли у устройства живой вход: отключённое остаётся в списке неактивным. */
  active: boolean;
  /** То самое устройство, с которого сейчас смотрят. */
  current: boolean;
}

/**
 * Токен агента (3.16.7): ключ, которым в трекер ходит не человек, а
 * Claude — с ноутбука сейчас, из интернета после этапа 15. Сам токен виден
 * один раз, при выпуске; дальше в списке только имя, хвост и следы.
 */
export interface ApiTokenSummary {
  id: string;
  name: string;
  scopes: ApiScope[];
  /** Последние символы токена — отличить один от другого, не раскрывая. */
  hint: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

/** Что человек решил на экране согласия для коннектора. */
export interface AgentConsent {
  client_id: string;
  redirect_uri: string;
  state?: string | undefined;
  code_challenge: string;
  scopes: ApiScope[];
  name?: string | undefined;
  decision: 'allow' | 'deny';
}

/** След одного вызова: когда, что и с какими задачами. */
export interface ApiCallSummary {
  at: string;
  method: string;
  path: string;
  status: number;
  refs: string[];
}

export interface Session {
  accessToken(): Promise<string>;
  refresh(): Promise<boolean>;
  /** Поднять сессию при запуске вкладки: refresh-cookie переживает перезагрузку. */
  restore(): Promise<Identity | undefined>;
  identity(): Identity | undefined;
  deviceId(): string | undefined;
  requestEmailLink(email: string): Promise<void>;
  signInWithCode(email: string, code: string): Promise<Identity>;
  signInWithPasskey(): Promise<Identity>;
  registerPasskey(name?: string): Promise<void>;
  /** Привязанные ключи. Ими же проверяется, что привязка удалась. */
  listPasskeys(): Promise<PasskeySummary[]>;
  /** Снять ключ. Последний сервер снять не даст, пока почта не подтверждена (3.16.6). */
  deletePasskey(id: string): Promise<void>;
  /** Устройства, с которых выполнен вход. */
  listDevices(): Promise<DeviceSummary[]>;
  /** Отключить устройство. Действует сразу — сервер гасит сессии и закрывает сокет. */
  revokeDevice(id: string): Promise<void>;
  /** Токены агента — живые и отозванные за последний месяц. */
  listTokens(): Promise<ApiTokenSummary[]>;
  /** Выпустить токен. Ответ — единственное место, где он виден целиком. */
  createToken(name: string, scopes: ApiScope[]): Promise<ApiTokenSummary & { token: string }>;
  /** Отозвать токен. Действует сразу и не задевает вход человека (3.16.7). */
  revokeToken(id: string): Promise<void>;
  /** Последние вызовы токеном — след, по которому видно, что делал агент. */
  listTokenCalls(id: string): Promise<ApiCallSummary[]>;
  /**
   * Решение на экране согласия (этап 15): сервер выписывает код для
   * коннектора или отказ, а приложению отдаёт адрес, куда уйти.
   */
  authorizeAgent(decision: AgentConsent): Promise<{ redirect: string }>;
  /**
   * Удалить учётную запись вместе с данными (3.18.7).
   *
   * Адрес передаётся не ради безопасности — токен и так на руках, — а чтобы
   * запрос нельзя было отправить не в ту запись, в которой человек нажимал.
   */
  deleteAccount(email: string): Promise<void>;
  signOut(): Promise<void>;
  onChange(listener: (identity: Identity | undefined) => void): () => void;
}

export interface SessionOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  now?: () => number;
}

/** Обновляемся заранее: токен, истёкший между проверкой и запросом, — гонка на ровном месте. */
const REFRESH_SKEW_MS = 30_000;

export class AuthError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
    this.code = code;
  }
}

export function createSession(options: SessionOptions = {}): Session {
  const base = (options.baseUrl ?? '').replace(/\/$/, '');
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const now = options.now ?? (() => Date.now());

  let accessToken: string | undefined;
  let expiresAt = 0;
  let identity: Identity | undefined;
  let deviceId: string | undefined;
  let refreshing: Promise<boolean> | undefined;

  const listeners = new Set<(identity: Identity | undefined) => void>();

  function announce(): void {
    for (const listener of listeners) listener(identity);
  }

  async function call<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
    const response = await doFetch(`${base}${path}`, {
      ...init,
      headers: {
        ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(token === undefined ? {} : { authorization: `Bearer ${token}` }),
        ...init.headers,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
      };
      throw new AuthError(
        response.status,
        body.error ?? 'auth_error',
        body.message ?? `запрос ${path} вернул ${String(response.status)}`,
      );
    }

    return (await response.json()) as T;
  }

  function accept(payload: { access_token: string; expires_in: number; device_id: string }): void {
    accessToken = payload.access_token;
    expiresAt = now() + payload.expires_in * 1000;
    deviceId = payload.device_id;
  }

  async function loadIdentity(): Promise<Identity> {
    identity = await call<Identity>('/v1/me', { method: 'GET' }, await tokenNow());
    announce();
    return identity;
  }

  async function tokenNow(): Promise<string> {
    if (accessToken !== undefined && expiresAt - REFRESH_SKEW_MS > now()) {
      return accessToken;
    }
    if (!(await refresh())) {
      throw new AuthError(401, 'no_session', 'сессии нет');
    }
    return accessToken ?? '';
  }

  /**
   * Обновление сессии — по одному на весь браузер, а не на вкладку.
   *
   * Refresh-токен одноразовый: вторая вкладка, пошедшая обновляться в ту же
   * секунду, приносит уже погашенный токен. Сервер прощает такую гонку в
   * узком окне, но полагаться на прощение вместо того, чтобы не создавать
   * гонку, — плохой размен. Web Locks решают это между вкладками одного
   * происхождения; где их нет, остаётся защита на сервере.
   */
  async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
    const locks = globalThis.navigator?.locks;
    if (locks === undefined) return fn();
    return locks.request('nedelnik-refresh', fn);
  }

  async function refresh(): Promise<boolean> {
    // Один запрос на обновление, сколько бы вызовов ни пришло: параллельные
    // ротации refresh-токена гасят друг друга.
    refreshing ??= withRefreshLock(async () => {
      try {
        accept(
          await call<{ access_token: string; expires_in: number; device_id: string }>(
            '/auth/refresh',
            { method: 'POST' },
          ),
        );
        return true;
      } catch (error) {
        accessToken = undefined;
        expiresAt = 0;

        // `false` означает ровно одно: сервер сказал, что сессии больше нет.
        // Обрыв связи — не то же самое, и путать их нельзя: приложение
        // показало бы экран входа всякий раз, когда просто нет сети.
        if (error instanceof AuthError && (error.status === 401 || error.status === 403)) {
          return false;
        }
        throw error;
      } finally {
        refreshing = undefined;
      }
    });

    return refreshing;
  }

  return {
    accessToken: tokenNow,
    refresh,

    async restore(): Promise<Identity | undefined> {
      if (!(await refresh())) return undefined;
      return loadIdentity();
    },

    identity: () => identity,
    deviceId: () => deviceId,

    async requestEmailLink(email: string): Promise<void> {
      // Пояс уходит вместе с адресом: примеры для новой записи заводятся до
      // первой синхронизации и должны попасть в неделю человека (3.16.10).
      await call('/auth/email', {
        method: 'POST',
        body: JSON.stringify({ email, timezone: deviceTimeZone() }),
      });
    },

    async signInWithCode(email: string, code: string): Promise<Identity> {
      accept(
        await call<{ access_token: string; expires_in: number; device_id: string }>('/auth/code', {
          method: 'POST',
          body: JSON.stringify({ email, code }),
        }),
      );
      return loadIdentity();
    },

    async signInWithPasskey(): Promise<Identity> {
      const optionsJson = await call<PublicKeyCredentialRequestOptionsJSON>(
        '/auth/passkey/login/options',
        { method: 'POST' },
      );

      // Ключи заводятся discoverable, поэтому почту на экране входа спрашивать
      // не нужно: достаточно Face ID.
      const response = await startAuthentication({ optionsJSON: optionsJson });

      accept(
        await call<{ access_token: string; expires_in: number; device_id: string }>(
          '/auth/passkey/login/verify',
          { method: 'POST', body: JSON.stringify({ response }) },
        ),
      );
      return loadIdentity();
    },

    async registerPasskey(name?: string): Promise<void> {
      const token = await tokenNow();
      const optionsJson = await call<PublicKeyCredentialCreationOptionsJSON>(
        '/auth/passkey/register/options',
        { method: 'POST' },
        token,
      );

      const response = await startRegistration({ optionsJSON: optionsJson });

      await call(
        '/auth/passkey/register/verify',
        {
          method: 'POST',
          body: JSON.stringify({ response, ...(name === undefined ? {} : { name }) }),
        },
        token,
      );
    },

    async listPasskeys(): Promise<PasskeySummary[]> {
      const body = await call<{ passkeys: PasskeySummary[] }>(
        '/v1/passkeys',
        { method: 'GET' },
        await tokenNow(),
      );
      return body.passkeys;
    },

    async deletePasskey(id: string): Promise<void> {
      await call(`/v1/passkeys/${encodeURIComponent(id)}`, { method: 'DELETE' }, await tokenNow());
    },

    async listDevices(): Promise<DeviceSummary[]> {
      const body = await call<{ devices: DeviceSummary[] }>(
        '/v1/devices',
        { method: 'GET' },
        await tokenNow(),
      );
      return body.devices;
    },

    async revokeDevice(id: string): Promise<void> {
      await call(`/v1/devices/${encodeURIComponent(id)}`, { method: 'DELETE' }, await tokenNow());
    },

    async listTokens(): Promise<ApiTokenSummary[]> {
      const body = await call<{ tokens: ApiTokenSummary[] }>(
        '/v1/tokens',
        { method: 'GET' },
        await tokenNow(),
      );
      return body.tokens;
    },

    async createToken(
      name: string,
      scopes: ApiScope[],
    ): Promise<ApiTokenSummary & { token: string }> {
      return call<ApiTokenSummary & { token: string }>(
        '/v1/tokens',
        { method: 'POST', body: JSON.stringify({ name, scopes }) },
        await tokenNow(),
      );
    },

    async revokeToken(id: string): Promise<void> {
      await call(`/v1/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' }, await tokenNow());
    },

    async authorizeAgent(decision: AgentConsent): Promise<{ redirect: string }> {
      return call<{ redirect: string }>(
        '/oauth/consent',
        { method: 'POST', body: JSON.stringify(decision) },
        await tokenNow(),
      );
    },

    async listTokenCalls(id: string): Promise<ApiCallSummary[]> {
      const body = await call<{ calls: ApiCallSummary[] }>(
        `/v1/tokens/${encodeURIComponent(id)}/calls`,
        { method: 'GET' },
        await tokenNow(),
      );
      return body.calls;
    },

    async deleteAccount(email: string): Promise<void> {
      await call(
        '/v1/account',
        { method: 'DELETE', body: JSON.stringify({ email }) },
        await tokenNow(),
      );
      accessToken = undefined;
      expiresAt = 0;
      identity = undefined;
      deviceId = undefined;
      announce();
    },

    async signOut(): Promise<void> {
      await call('/auth/logout', { method: 'POST' }).catch(() => undefined);
      accessToken = undefined;
      expiresAt = 0;
      identity = undefined;
      deviceId = undefined;
      announce();
    },

    onChange(listener): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
